import React, { useEffect, useState } from 'react';
import { ModalSidebarContent } from '../../../components/ModalSidebar';
import {
  DocumentVisiblity,
  useUpdateWorkflowItemFileRequestMutation,
  WorkflowItemStatus as WorkflowItemStatusGenerated,
  UpdateWorkflowItemFileRequestInput,
  FileInput,
} from '../../../__generated__/graphql';

import { mapFileOnServer } from './mappings';
import { WorkflowItemCommonFields } from './WorkflowItemCommonFields';
import { WorkflowItemStatusAutoSave } from './WorkflowItemStatusAutoSave';
import { WorkflowItemFileField } from './WorkflowItemFileField';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import {
  WorkflowItemFileRequest,
  WorkflowItemStatus,
} from '../WorkflowItems/types';
import { FileOnServer } from './types';
import { useToasts } from '../../../../../context/Toast';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';

type Props = {
  workflowItem: WorkflowItemFileRequest;
  fileOnServer: FileOnServer | null;
  uploadedFileVisibility: DocumentVisiblity | null;
  visibilityOptions?: DocumentVisiblity[];
};

type SaveState = {
  saving: boolean;
  saved: boolean;
};

type FileSaveState = SaveState & {
  value: FileOnServer | null;
};

export const WorkflowItemFileRequestForm: React.FC<Props> = ({
  workflowItem,
  fileOnServer,
  uploadedFileVisibility,
  visibilityOptions,
}) => {
  const { addError } = useToasts();
  const t = useNamespacedTranslation('documents');
  const [updateItem] = useUpdateWorkflowItemFileRequestMutation();

  const [isStatusLoading, setIsStatusLoading] = useState<boolean>(false);

  const [fileState, setFileState] = useState<FileSaveState>({
    saving: false,
    saved: false,
    value: fileOnServer,
  });

  const [fileVisibility, setFileVisibility] = useState<DocumentVisiblity>(
    uploadedFileVisibility ?? DocumentVisiblity.HrManagerEmployee,
  );

  useEffect(() => {
    setFileVisibility(
      uploadedFileVisibility ?? DocumentVisiblity.HrManagerEmployee,
    );
    setFileState((state) => ({ ...state, value: fileOnServer }));
  }, [workflowItem.id, fileOnServer, uploadedFileVisibility]);

  const errorHandler = useMutationErrorHandler();

  const handleStatusChange = (status: WorkflowItemStatus) => {
    setIsStatusLoading(true);
    updateItem({
      variables: {
        input: {
          id: workflowItem.id,
          status: status as WorkflowItemStatusGenerated,
          uploadFile: null,
          deleteFileId: null,
          uploadedFileVisibility: null,
        },
      },
    })
      .catch(errorHandler)
      .finally(() => {
        setIsStatusLoading(false);
      });
  };

  const handleSelectFile = (uploadFile: FileInput) =>
    updateFile(uploadFile, null, fileVisibility);

  const handleDeleteFile = (deleteFileId: string) => {
    if (
      fileVisibility === DocumentVisiblity.Hr ||
      fileVisibility === DocumentVisiblity.HrManager
    ) {
      addError(t('deleteFileError'));
      return;
    }
    updateFile(null, deleteFileId, null);
  };

  const updateFile = (
    uploadFile: FileInput | null,
    deleteFileId: string | null,
    visibility: DocumentVisiblity | null,
    previousVisibility?: DocumentVisiblity,
  ) => {
    setFileState((fileState) => ({ ...fileState, saving: true, saved: false }));

    let status = workflowItem.status;
    if (
      !workflowItem.readonly &&
      status === WorkflowItemStatusGenerated.NotStarted
    ) {
      status = WorkflowItemStatusGenerated.InProgress;
      setIsStatusLoading(true);
    }

    const graphQLFileInput = uploadFile
      ? {
          fileName: uploadFile.fileName,
          fileContentBase64: uploadFile.fileContentBase64,
        }
      : null;

    const shouldUpdateVisibility = !deleteFileId;

    const variables = {
      input: {
        id: workflowItem.id,
        uploadFile: graphQLFileInput,
        deleteFileId,
        status: status as WorkflowItemStatusGenerated,
        uploadedFileVisibility: shouldUpdateVisibility
          ? visibility ?? fileVisibility
          : null,
      } as UpdateWorkflowItemFileRequestInput,
    };

    return updateItem({
      variables,
    })
      .then((response) => {
        const record =
          response.data?.workflow.updateWorkflowItemFileRequest.record;

        if (!record) {
          setFileState((fileState) => ({
            ...fileState,
            saving: false,
            saved: false,
          }));
          if (previousVisibility !== undefined) {
            setFileVisibility(previousVisibility);
          }
          return;
        }

        const uploadedFile = record.uploadedFile;
        const updatedVisibility =
          (record as { uploadedFileVisibility?: DocumentVisiblity })
            .uploadedFileVisibility ??
          visibility ??
          fileVisibility;

        setFileState((fileState) => ({
          value: uploadedFile
            ? mapFileOnServer(uploadedFile)
            : fileState.value
            ? { ...fileState.value, visibility: updatedVisibility ?? null }
            : null,
          saving: false,
          saved: true,
        }));

        if (updatedVisibility) {
          setFileVisibility(updatedVisibility);
        }
      })
      .catch((error) => {
        setFileState((fileState) => ({
          ...fileState,
          saving: false,
          saved: false,
        }));
        if (previousVisibility !== undefined) {
          setFileVisibility(previousVisibility);
        }
        errorHandler(error);
      })
      .finally(() => {
        setIsStatusLoading(false);
      });
  };

  const handleVisibilityChange = (visibility: DocumentVisiblity) => {
    const previousVisibility = fileVisibility;
    setFileVisibility(visibility);

    if (fileState.value) {
      updateFile(null, null, visibility, previousVisibility);
    }
  };

  const onStatusLoading = (isStatusLoading: boolean) => {
    setIsStatusLoading(isStatusLoading);
  };

  return (
    <ModalSidebarContent
      body={
        <>
          <WorkflowItemCommonFields
            workflowItem={workflowItem}
            onStatusLoading={onStatusLoading}
          />
          <WorkflowItemFileField
            fileFormat={workflowItem.fileFormat}
            file={fileState.value}
            saving={fileState.saving}
            saved={fileState.saved}
            readonly={workflowItem.readonly}
            onSelectFile={handleSelectFile}
            onDeleteFile={handleDeleteFile}
            visibility={fileVisibility}
            onVisibilityChange={handleVisibilityChange}
            visibilityOptions={visibilityOptions}
          />

          <WorkflowItemStatusAutoSave
            value={workflowItem.status}
            onChange={handleStatusChange}
            readonly={workflowItem.readonly}
            saving={isStatusLoading}
            saved={!isStatusLoading}
          />
        </>
      }
    />
  );
};
